import { ComponentPropsWithoutRef, FC, Dispatch, useEffect, useState } from 'react';
import { BiFilterAlt as FilterIcon } from 'react-icons/bi';
import { ActionIcon, Button, Select, Group, Popover } from '@mantine/core';
import { useIntlLight } from '../../../../../i18n';
import { actionTypes } from '../../../../types';
import { selectDropdownStyles } from './getSelectDropdownStyles';
import useFilterStyles from './useFilterStyles';

export type SelectItemProps = {
  label: string;
  value: string;
  color: string;
} & ComponentPropsWithoutRef<'div'>;

export default function SelectFilter({
  columnName,
  state,
  dispatch,
  data,
  label,
  placeholder,
  searchable = false,
  disableOnActiveFilter = false,
  itemComponent,
}: {
  columnName: string;
  state: any;
  dispatch: Dispatch<any>;
  data: any;
  label: string;
  placeholder?: string;
  searchable?: boolean;
  disableOnActiveFilter?: boolean;
  itemComponent?: FC<SelectItemProps>;
}) {
  const { t } = useIntlLight();
  const [opened, setOpened] = useState(false);
  const filter = (state?.filters || []).find((_filter: any) => _filter.property === columnName) || {
    value: '',
  };
  const [stateFilter, setStateFilter] = useState<string | null>(filter.value || null);
  const { classes } = useFilterStyles({ isFilterEnabled: Boolean(filter?.value) });
  const isFilterLocked = disableOnActiveFilter && Boolean(filter?.value);
  const isApplyDisabled = !stateFilter && !filter.value;

  useEffect(() => {
    if (opened) {
      setStateFilter(filter.value);
    }
  }, [opened, filter.value]);

  const handleClose = () => {
    setOpened(false);
  };

  const handleClear = () => {
    setStateFilter(null);
    dispatch({
      type: actionTypes.SET_FILTER,
      payload: { property: columnName, value: '' },
    });
    setOpened(false);
  };

  const handleApply = () => {
    dispatch({
      type: actionTypes.SET_FILTER,
      payload: { property: columnName, value: stateFilter ?? '' },
    });
    setOpened(false);
  };

  return (
    <Popover
      opened={opened}
      onClose={handleClose}
      position="bottom"
      transition="scale-y"
      withinPortal
    >
      <Popover.Target>
        <ActionIcon
          size="sm"
          className={classes.filterIconButton}
          color={filter?.value ? 'blue' : 'gray'}
          onClick={() => setOpened(o => !o)}
          variant="filled"
        >
          <FilterIcon />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Select
          label={label}
          placeholder={placeholder ?? t('app.search')}
          data={data}
          value={stateFilter}
          onChange={value => setStateFilter(value)}
          clearable={searchable && !isFilterLocked}
          searchable={searchable}
          disabled={isFilterLocked}
          itemComponent={itemComponent}
          styles={{
            ...selectDropdownStyles,
            input: {
              marginRight: 16,
            },
          }}
        />
        <Group position="apart" mt="sm">
          <Button color="red" onClick={handleClear} disabled={!filter.value}>
            {t('app.clear')}
          </Button>
          {!isFilterLocked && (
            <Button onClick={handleApply} disabled={isApplyDisabled}>
              {t('app.apply')}
            </Button>
          )}
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
}
